import React from "react";
import {
  AppstoreOutlined,
  BankOutlined,
  FileTextOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import { colors, fontSize } from "~/styles/tokens";
import { JourneyWhyExpander } from "./JourneyWhyExpander";

const { Title, Paragraph } = Typography;

const LAYERS = [
  {
    key: "property",
    icon: <HomeOutlined />,
    color: colors.actionPrimary,
    labelKey: "Property" as const,
    bodyKey: "JourneyGuide_Assets_Property_Body" as const,
  },
  {
    key: "building",
    icon: <BankOutlined />,
    color: colors.statusSuccess,
    labelKey: "Building" as const,
    bodyKey: "JourneyGuide_Assets_Building_Body" as const,
  },
  {
    key: "unit",
    icon: <AppstoreOutlined />,
    color: colors.actionSecondary,
    labelKey: "Unit" as const,
    bodyKey: "JourneyGuide_Assets_Unit_Body" as const,
  },
] as const;

interface AssetsJourneySectionProps {
  isMobile: boolean;
}

/**
 * Assets — portfolio hierarchy stack + unit plans callout.
 */
export const AssetsJourneySection: React.FC<AssetsJourneySectionProps> = ({
  isMobile,
}) => {
  const { t } = useTranslation();

  return (
    <div className={`journey-content${isMobile ? " is-mobile" : ""}`}>
      <Title level={isMobile ? 3 : 2} style={{ marginTop: 0 }}>
        {t("JourneyGuide_Assets_Title")}
      </Title>
      <Paragraph
        className="journey-lead"
        style={{
          fontSize: isMobile ? fontSize.body : fontSize.emphasis,
        }}
      >
        {t("JourneyGuide_Assets_Description")}
      </Paragraph>

      <ol className={`journey-stack${isMobile ? " is-mobile" : ""}`}>
        {LAYERS.map((layer, index) => (
          <li
            key={layer.key}
            className="journey-stack-layer"
            style={{
              ["--stack-color" as string]: layer.color,
              ["--stack-index" as string]: index,
            }}
          >
            <span className="journey-stack-badge" aria-hidden>
              {layer.icon}
            </span>
            <div className="journey-stack-copy">
              <div className="journey-stack-label">{t(layer.labelKey)}</div>
              <p className="journey-stack-body">{t(layer.bodyKey)}</p>
            </div>
            {!isMobile && (
              <span className="journey-stack-depth" aria-hidden>
                {String(index + 1).padStart(2, "0")}
              </span>
            )}
          </li>
        ))}
      </ol>

      <div className="journey-sidecar">
        <span className="journey-sidecar-icon" aria-hidden>
          <FileTextOutlined />
        </span>
        <div className="journey-sidecar-copy">
          <div className="journey-sidecar-label">
            {t("JourneyGuide_Assets_UnitPlans_Label")}
          </div>
          <p className="journey-sidecar-body">
            {t("JourneyGuide_Assets_UnitPlans_Body")}
          </p>
        </div>
      </div>

      <JourneyWhyExpander
        summaryKey="JourneyGuide_Assets_Why_Summary"
        detailKey="JourneyGuide_Assets_Why_Detail"
      />
    </div>
  );
};
